import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Polygon, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Radio,
  Activity,
  MapPin,
  Clock,
  RefreshCw,
  TriangleAlert,
  MountainSnow,
  CloudRain,
  Zap,
  Loader2,
  AlertOctagon,
  Map as MapIconLucide,
  FilePlus2,
  Layers,
  Maximize2,
  Minimize2,
  Hash,
  CheckCircle2,
  FileText,
  Users,
  ShieldAlert,
  Building2,
  ArrowLeft,
} from "lucide-react";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import GridItem from "../../../../Components/UiComponents/GridItem/GridItem";
import { getData, postDataWithBody } from "../../../../Services/Services";
import * as EndPoints from "../../../../Services/EndPoints";

// ─────────────────────────────────────────────────────────────────────────
// Sachet CAP Alerts — reads the two 3rd-party feeds documented for this
// app: NDMA Sachet CAP alerts for Ladakh and the standalone earthquake
// alert service. Both are plain GET endpoints returning a JSON array; this
// page is mostly a read-only viewer over that raw API data, aside from the
// per-card "Create Alert" action on each earthquake card, which posts that
// single record into the app's own alerting flow.
// ─────────────────────────────────────────────────────────────────────────

const SEVERITY_COLORS = {
  Extreme: "#8B0000",
  Severe: "#DC2626",
  Moderate: "#F59E0B",
  Minor: "#EA580C",
  Unknown: "#64748B",
};

const MSGTYPE_COLORS = {
  Alert: { bg: "#EFF6FF", fg: "#1D4ED8", border: "#BFDBFE" },
  Update: { bg: "#FFFBEB", fg: "#B45309", border: "#FDE68A" },
  Cancel: { bg: "#F1F5F9", fg: "#475569", border: "#E2E8F0" },
};

// Plain-language stand-ins for the raw CAP vocabulary (urgency/certainty/
// msgType) — the feed's own wording (e.g. "Future", "Possible", "Alert")
// reads like a spec, not something a duty officer can act on at a glance.
const MSGTYPE_LABELS = {
  Alert: "Alert",
  Update: "Updated",
  Cancel: "Cancelled",
};

const URGENCY_LABELS = {
  Immediate: "Happening now",
  Expected: "Expected soon",
  Future: "Expected later",
  Past: "Already passed",
};

const CERTAINTY_LABELS = {
  Observed: "Confirmed",
  Likely: "Likely",
  Possible: "Possible",
  Unlikely: "Unlikely",
};

// Earthquake intensity zones don't ship with a plain severity label, only a
// raw scale value — the zones are always ordered widest-radius-first, so
// position in the list stands in for how strong the shaking is.
const SHAKING_LABELS = ["Light shaking", "Moderate shaking", "Strong shaking", "Severe shaking"];

function humanize(map, value, fallback = "Not specified") {
  if (!value) return fallback;
  return map[value] || value;
}

function eventIcon(eventName = "") {
  const e = eventName.toLowerCase();
  if (e.includes("avalanche")) return { Icon: MountainSnow, color: "#9333EA" };
  if (e.includes("rain") || e.includes("thunder") || e.includes("storm") || e.includes("cloud"))
    return { Icon: CloudRain, color: "#0284C7" };
  return { Icon: TriangleAlert, color: "#F57F17" };
}

// Groups a CAP alert's free-text `event` (e.g. "Heavy Rain", "Avalanche
// Warning", "Cloud Burst") into the hazard buckets the sub-tabs filter on.
// Anything that matches none of the named buckets still shows under "All".
function capEventCategory(eventName = "") {
  const e = eventName.toLowerCase();
  if (e.includes("avalanche")) return "avalanche";
  if (e.includes("cloudburst") || e.includes("cloud burst")) return "cloudburst";
  if (e.includes("flood")) return "flood";
  return "other";
}

const CAP_CATEGORIES = [
  { key: "all", label: "All" },
  { key: "flood", label: "Floods" },
  { key: "cloudburst", label: "Cloudburst" },
  { key: "avalanche", label: "Avalanche" },
];

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// The earthquake feed ships timestamps as a Java Date#toString(), e.g.
// "Wed Aug 26 03:30:23 IST 2026" — day-of-week/month/day/time/zone/year,
// with the year last instead of the ISO order `new Date()` expects, so it
// parses as Invalid Date otherwise.
function parseJavaDateString(value) {
  const match = /^\w{3}\s+(\w{3})\s+(\d{1,2})\s+(\d{2}):(\d{2}):(\d{2})\s+\S+\s+(\d{4})$/.exec(value);
  if (!match) return null;
  const [, monthName, day, hh, mm, ss, year] = match;
  const monthIndex = MONTH_ABBR.indexOf(monthName);
  if (monthIndex === -1) return null;
  return new Date(Number(year), monthIndex, Number(day), Number(hh), Number(mm), Number(ss));
}

function formatDateTime(value) {
  if (!value) return "—";
  let d = new Date(value);
  if (isNaN(d.getTime()) && typeof value === "string") {
    d = parseJavaDateString(value) || d;
  }
  if (isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  let h = d.getHours();
  const min = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${dd}-${mm}-${yy}, ${String(h).padStart(2, "0")}:${min} ${ampm}`;
}

// "Last Updated" stamp for the page header — DD-MM-YYYY hh:mm AM/PM,
// fed from the /config/server-time response so it reflects the server's
// clock rather than the browser's.
function formatServerStamp(value) {
  if (!value) return "—";
  let d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime()) && typeof value === "string") {
    d = parseJavaDateString(value) || d;
  }
  if (isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  let h = d.getHours();
  const min = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${dd}-${mm}-${yy} ${String(h).padStart(2, "0")}:${min} ${ampm}`;
}

// The alerts feeds update infrequently, so the page auto-refreshes on a
// fixed 5-minute cadence.
const AUTO_REFRESH_MS = 2 * 60 * 1000;

// CAP polygonData is a single space-separated "lat,lng lat,lng ..." ring —
// already in Leaflet's [lat, lng] order, so no coordinate swap needed.
function parseCapPolygonPoints(polygonData) {
  if (!polygonData || typeof polygonData !== "string") return [];
  return polygonData
    .trim()
    .split(/\s+/)
    .map((pair) => {
      const [lat, lng] = pair.split(",").map(Number);
      return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
    })
    .filter(Boolean);
}

// Earthquake polygonData is a JSON array of intensity zones, each carrying a
// GeoJSON MultiPolygon under `coordinates.coordinates` — GeoJSON orders
// points [lng, lat], the opposite of Leaflet's [lat, lng], so every point
// gets swapped here.
function safeParseIntensityZones(polygonData) {
  if (!polygonData || typeof polygonData !== "string") return [];
  try {
    const parsed = JSON.parse(polygonData);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((zone, index) => {
      const rings = [];
      const multiPolygon = zone?.coordinates?.coordinates;
      if (Array.isArray(multiPolygon)) {
        multiPolygon.forEach((polygon) => {
          if (!Array.isArray(polygon)) return;
          polygon.forEach((ring) => {
            if (!Array.isArray(ring)) return;
            const points = ring
              .map((pt) => (Array.isArray(pt) && pt.length >= 2 ? [pt[1], pt[0]] : null))
              .filter(Boolean);
            if (points.length > 2) rings.push(points);
          });
        });
      }
      return {
        name: zone.name,
        intensity: zone.intensity,
        color: zone.color,
        radiusKm: typeof zone.radius === "number" ? (zone.radius / 1000).toFixed(1) : null,
        shakingLabel: SHAKING_LABELS[Math.min(index, SHAKING_LABELS.length - 1)],
        rings,
      };
    });
  } catch (_) {
    return [];
  }
}

// react-leaflet's default marker icon relies on image paths that webpack
// often can't resolve, leaving a broken-image glyph — a small divIcon
// sidesteps that entirely (same fix used for the pin markers elsewhere in
// this app, e.g. CreateAlert.js's makePinIcon).
function makeEpicenterIcon() {
  return L.divIcon({
    className: "",
    html: `
      <div style="
        width: 22px; height: 22px; border-radius: 50%;
        background: #DC2626;
        border: 3px solid #fff;
        box-shadow: 0 2px 8px rgba(0,0,0,0.4);
      "></div>
    `,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

// Teal map pin for the linked incident's location (from the alert-detail
// endpoint) — deliberately different from the red epicenter dot so the two
// don't read as the same thing when both are on the map.
function makeIncidentIcon() {
  return L.divIcon({
    className: "",
    html: `
      <div style="
        position: relative; width: 26px; height: 34px;
      ">
        <div style="
          position: absolute; left: 50%; top: 0; transform: translateX(-50%);
          width: 26px; height: 26px; border-radius: 50% 50% 50% 0;
          background: #146680; border: 2px solid #fff;
          box-shadow: 0 3px 8px rgba(0,0,0,0.35);
          transform-origin: center; rotate: -45deg;
        "></div>
        <div style="
          position: absolute; left: 50%; top: 8px; transform: translateX(-50%);
          width: 9px; height: 9px; border-radius: 50%; background: #fff;
        "></div>
      </div>
    `,
    iconSize: [26, 34],
    iconAnchor: [13, 30],
    popupAnchor: [0, -28],
  });
}

// The alert-detail endpoint returns java.time timestamps as a numeric array
// [year, month(1-based), day, hour, minute, second, nanos]. Turn that into
// the same display format as formatDateTime.
function formatLdtArray(value) {
  if (!Array.isArray(value) || value.length < 3) return null;
  const [y, mo, d, h = 0, mi = 0, s = 0] = value;
  const dt = new Date(y, (mo || 1) - 1, d, h, mi, s);
  return isNaN(dt.getTime()) ? null : formatDateTime(dt);
}

function isoFromLdtArray(value) {
  if (typeof value === "string") return value;
  if (!Array.isArray(value) || value.length < 3) return new Date().toISOString();
  const [y, mo, d, h = 0, mi = 0, s = 0] = value;
  const dt = new Date(y, (mo || 1) - 1, d, h, mi, s);
  return isNaN(dt.getTime()) ? new Date().toISOString() : dt.toISOString();
}

// The earthquake feed's warning text ends with ", Location: <place>" — pull
// that place name out so the card can show it instead of raw lat/long.
function locationFromWarning(message) {
  if (!message || typeof message !== "string") return null;
  const m = message.match(/Location:\s*([^\n]+)/i);
  if (!m) return null;
  return m[1].trim().replace(/[.\s]+$/, "") || null;
}

// Recreates the router-state shape the "Acknowledge Disaster" screen
// (AccidentViewer) reads from `location.state.notificationData`, out of this
// endpoint's `disasterAlert` object — mirrors the mapping in
// AccidentHistoryTable.handleViewAlertDetails so the screen renders the same
// way whether it's reached from the alerts list or from here.
function disasterAlertToNotification(da, seqId) {
  if (!da) return null;
  return {
    id: da.id,
    caseId: seqId || da.disasterAlertId || (da.id != null ? `DMS-${da.id}` : undefined),
    notificationTitle: da.hazardType,
    notificationMessage: da.alertDescription,
    createdAt: isoFromLdtArray(da.createdAt),
    isRead: true,
    priority: (da.severity || "").split("–").pop().trim().toUpperCase() || "MEDIUM",
    acknowledged: Boolean(da.acknowledged),
    acknowledgedName: da.acknowledgedName,
    sensorNetwork: da.sensorNetwork,
    acktime: da.acknowledgedTime || da.acknowledgedTimestamp || null,
    disasterType: da.hazardType,
    location: da.regionName,
    actionTaken: da.actionTaken,
    measuredValue: da.measuredValue,
    thresholdValue: da.thresholdValue,
    unit: da.unit,
    alertDescription: da.alertDescription,
    severity: da.severity,
    latitude: da.latitude ?? 34.1526,
    longitude: da.longitude ?? 77.5771,
    affectedArea: da.affectedArea || "N/A",
    affectedPopulation: da.affectedPopulation || "N/A",
    reportedBy: da.reportedBy || "Disaster Management System",
    contact: da.emergencyContact || "+91 1982-XXXXX",
    disasterAlertId: seqId || da.disasterAlertId,
    assessments: da.assessments || [],
    // SACHET_CAP / SACHET_EARTHQUAKE = NDMA, SENSOR = Manual (see
    // AccidentViewer.js's getAlertTypeBadge).
    sourceType: da.sourceType,
  };
}

// Pans/zooms the map to fit everything passed in, once it's available —
// MapContainer's `center`/`zoom` only apply on initial mount. Handles the
// marker-only case (a single point) by centring instead of fitting bounds.
const FitBoundsToPolygons = ({ pointGroups, fallbackCenter }) => {
  const map = useMap();
  useEffect(() => {
    const allPoints = pointGroups.flat();
    if (allPoints.length > 1) {
      map.fitBounds(L.latLngBounds(allPoints), { padding: [24, 24] });
    } else if (allPoints.length === 1) {
      map.setView(allPoints[0], 9);
    } else if (fallbackCenter) {
      map.setView(fallbackCenter, 7);
    }
  }, [pointGroups, fallbackCenter, map]);
  return null;
};

// Base-layer options for the inline maps — same set/tiles as the
// "Acknowledge Disaster" map (AccidentViewer.js) so both screens behave
// the same way.
const MAP_LAYERS = [
  { id: "road", name: "Road View" },
  { id: "satellite", name: "Satellite View" },
  { id: "terrain", name: "Terrain View" },
  { id: "hybrid", name: "Hybrid View" },
];

function getTileLayerUrl(layerId) {
  const urls = {
    road: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    satellite: "https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
    terrain: "https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}",
    hybrid: "https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}",
  };
  return urls[layerId] || urls.road;
}

// Layers menu shown top-right on the inline maps (mirrors AccidentViewer's
// CustomLayersControl, restyled to this page's teal accent).
const MapLayersControl = ({ activeLayer, onLayerChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div style={{ position: "absolute", top: "10px", right: "10px", zIndex: 1000 }}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        title="Map Layers"
        style={{
          width: "36px",
          height: "36px",
          border: "none",
          borderRadius: "8px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          transition: "all 0.2s ease",
          background: isOpen ? "#146680" : "#fff",
        }}
      >
        <Layers size={18} color={isOpen ? "#fff" : "#374151"} strokeWidth={2.5} />
      </button>
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "44px",
            right: 0,
            background: "#fff",
            borderRadius: "10px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
            minWidth: "190px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "10px",
              borderBottom: "1px solid #E5E7EB",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#F0F6FB",
            }}
          >
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#0F2D42" }}>Map Layers</span>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              style={{ background: "none", border: "none", fontSize: "15px", color: "#9CA3AF", cursor: "pointer", lineHeight: 1 }}
            >
              ✕
            </button>
          </div>
          <div style={{ padding: "6px" }}>
            {MAP_LAYERS.map((layer) => (
              <div
                key={layer.id}
                onClick={() => {
                  onLayerChange(layer.id);
                  setIsOpen(false);
                }}
                style={{
                  padding: "9px 10px",
                  cursor: "pointer",
                  borderRadius: "6px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "2px",
                  background: activeLayer === layer.id ? "#EFF6FF" : "transparent",
                  borderLeft: activeLayer === layer.id ? "3px solid #146680" : "3px solid transparent",
                }}
              >
                <span style={{ fontSize: "12.5px", fontWeight: 500, color: "#374151" }}>{layer.name}</span>
                {activeLayer === layer.id && <span style={{ color: "#146680", fontSize: "13px" }}>✓</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Full-screen toggle for the inline maps — requests fullscreen on the map
// wrapper and nudges Leaflet to recalc its size afterwards (same approach
// as AccidentViewer's MapFullscreenControl).
const MapFullscreenControl = ({ targetRef }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => window.dispatchEvent(new Event("resize")), 120);
    };
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, []);

  const toggleFullscreen = () => {
    const el = targetRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  return (
    <button
      type="button"
      onClick={toggleFullscreen}
      title={isFullscreen ? "Exit full screen" : "Full screen"}
      style={{
        position: "absolute",
        top: "54px",
        right: "10px",
        zIndex: 1000,
        width: "36px",
        height: "36px",
        borderRadius: "8px",
        border: isFullscreen ? "2px solid #146680" : "1px solid rgba(0,0,0,0.15)",
        background: isFullscreen ? "#EFF6FF" : "#fff",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        transition: "all 0.2s ease",
      }}
    >
      {isFullscreen ? (
        <Minimize2 size={16} color="#146680" strokeWidth={2.2} />
      ) : (
        <Maximize2 size={16} color="#1e3a5f" strokeWidth={2.2} />
      )}
    </button>
  );
};

// Shared inline map used by both the CAP and earthquake cards to draw the
// alert's polygon boundary (or, for earthquakes, its concentric intensity
// rings). Carries a layers switcher and a full-screen toggle, matching the
// "Acknowledge Disaster" map.
const AlertPolygonMap = ({ polygons, markerPosition, incidentMarker }) => {
  const wrapperRef = useRef(null);
  const [activeLayer, setActiveLayer] = useState("road");
  const nonEmpty = polygons.filter((p) => p.positions && p.positions.length > 2);
  const incidentPos = incidentMarker && incidentMarker.position;
  // The incident pin already carries the "you are here" meaning, so drop the
  // plain epicenter dot when both would land in roughly the same spot.
  const showEpicenter = markerPosition && !incidentPos;

  if (nonEmpty.length === 0 && !incidentPos && !markerPosition) {
    return (
      <div
        style={{
          marginTop: "10px",
          padding: "26px 16px",
          textAlign: "center",
          borderRadius: "10px",
          border: "1px solid #EEF2F6",
          background: "#F8FAFC",
          color: "#94A3B8",
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        No location coordinates available to plot for this alert.
      </div>
    );
  }
  const initialCenter = incidentPos || markerPosition || (nonEmpty[0] && nonEmpty[0].positions[0]) || [34.15, 77.57];
  return (
    <div
      ref={wrapperRef}
      style={{
        marginTop: "10px",
        height: "340px",
        borderRadius: "10px",
        overflow: "hidden",
        border: "1px solid #EEF2F6",
        position: "relative",
        background: "#fff",
      }}
    >
      <MapContainer center={initialCenter} zoom={8} attributionControl={false} style={{ height: "100%", width: "100%" }}>
        <TileLayer
          key={activeLayer}
          url={getTileLayerUrl(activeLayer)}
          subdomains={activeLayer === "road" ? ["a", "b", "c"] : ["mt0", "mt1", "mt2", "mt3"]}
          maxZoom={20}
        />
        {nonEmpty.map((p, i) => (
          <Polygon
            key={i}
            positions={p.positions}
            pathOptions={{
              color: p.color || "#146680",
              fillColor: p.color || "#146680",
              fillOpacity: p.fillOpacity ?? 0.18,
              weight: 2,
            }}
          />
        ))}
        {showEpicenter && <Marker position={markerPosition} icon={makeEpicenterIcon()} />}
        {incidentPos && (
          <Marker position={incidentPos} icon={makeIncidentIcon()}>
            {incidentMarker.render && (
              <Popup minWidth={384} maxWidth={400} className="cap-incident-popup">
                {incidentMarker.render}
              </Popup>
            )}
          </Marker>
        )}
        <FitBoundsToPolygons
          pointGroups={[...nonEmpty.map((p) => p.positions), ...(incidentPos ? [[incidentPos]] : [])]}
          fallbackCenter={initialCenter}
        />
      </MapContainer>
      <MapLayersControl activeLayer={activeLayer} onLayerChange={setActiveLayer} />
      <MapFullscreenControl targetRef={wrapperRef} />
    </div>
  );
};

const mapToggleBtnStyle = (active) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  border: `1px solid ${active ? "#146680" : "#CBD9E3"}`,
  background: active ? "#146680" : "#fff",
  color: active ? "#fff" : "#146680",
  fontSize: "11.5px",
  fontWeight: 700,
  cursor: "pointer",
  padding: "7px 14px",
  borderRadius: "8px",
  whiteSpace: "nowrap",
  flexShrink: 0,
  transition: "all 0.15s ease",
});

const cardStyle = {
  background: "#fff",
  border: "1px solid #E9EEF3",
  borderRadius: "14px",
  padding: "16px 18px",
  boxShadow: "0 1px 2px rgba(15,45,66,0.04), 0 6px 20px rgba(15,45,66,0.05)",
};

// Tinted bar across the top of every alert card — bleeds out to the card
// edges over cardStyle's 16px/18px padding, rounds with the card, and sits
// above the card body.
const cardHeaderStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "12px",
  margin: "-16px -18px 0",
  padding: "12px 18px",
  background: "#EBF4FA",
  borderBottom: "1px solid #E1EAF2",
  borderRadius: "14px 14px 0 0",
  flexWrap: "wrap",
};

const badgeStyle = (bg, fg, border) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  fontSize: "11px",
  fontWeight: 700,
  padding: "3px 9px",
  borderRadius: "999px",
  background: bg,
  color: fg,
  border: `1px solid ${border || bg}`,
  whiteSpace: "nowrap",
});

const confirmModalBtnStyle = (primary, creating) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  height: "34px",
  padding: "0 16px",
  fontSize: "12.5px",
  fontWeight: primary ? 700 : 600,
  borderRadius: "6px",
  border: primary ? "1px solid #146680" : "1px solid #CBD5E1",
  background: primary ? "#146680" : "#fff",
  color: primary ? "#fff" : "#334155",
  cursor: creating ? "default" : "pointer",
  opacity: creating && !primary ? 0.6 : creating ? 0.85 : 1,
  boxSizing: "border-box",
});

const createAlertBtnStyle = (creating, created) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  border: "none",
  borderRadius: "999px",
  padding: "3px 10px",
  fontSize: "11px",
  fontWeight: 700,
  whiteSpace: "nowrap",
  cursor: creating || created ? "default" : "pointer",
  background: created ? "#DCFCE7" : "#146680",
  color: created ? "#16A34A" : "#fff",
  opacity: creating ? 0.7 : 1,
});

const metaLabelStyle = {
  fontSize: "12.5px",
  fontWeight: 600,
  color: "#64748B",
  letterSpacing: "0",
  marginBottom: "3px",
};

const metaValueStyle = {
  fontSize: "12.5px",
  fontWeight: 600,
  color: "#334155",
  wordBreak: "break-word",
};

// Light rounded panel that groups the alert's detail fields into an even
// two/three-column grid — keeps the card body tidy instead of a loose run
// of label/value pairs.
const metaPanelStyle = {
  marginTop: "12px",
  padding: "12px 14px",
  background: "#F8FAFC",
  border: "1px solid #EEF2F6",
  borderRadius: "10px",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: "12px 16px",
};

const monoValueStyle = {
  ...metaValueStyle,
  fontFamily: "'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
  letterSpacing: "0.01em",
};

// One label/value cell in the detail grid. `mono` renders identifiers in a
// monospace face so codes like NDMA-20260827-14 stay easy to read/compare.
const MetaField = ({ label, icon: LabelIcon, value, mono }) => (
  <div>
    <div style={metaLabelStyle}>
      {LabelIcon && <LabelIcon size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />}
      {label}
    </div>
    <div style={mono ? monoValueStyle : metaValueStyle}>{value}</div>
  </div>
);

const linkValueStyle = {
  ...metaValueStyle,
  color: "#146680",
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  cursor: "pointer",
  background: "none",
  border: "none",
  padding: 0,
  textAlign: "left",
};

const LinkValue = ({ onClick, title, children, loading }) => (
  <button type="button" onClick={onClick} disabled={loading} style={{ ...linkValueStyle, opacity: loading ? 0.6 : 1 }} title={title}>
    {loading && <Loader2 size={11} className="spin-caa" style={{ marginRight: "4px", verticalAlign: "-1px" }} />}
    {children}
  </button>
);

// Green confirmation panel shown once SACHET reports that this alert has
// already been turned into a pre-disaster alert / incident in the app —
// surfaces the linked record ids and links straight to their screens (the
// Acknowledge Disaster screen for the alert, View Incident for the incident)
// so a duty officer can jump to the record instead of re-creating it.
const CreatedRecordsPanel = ({ alert, detail }) => {
  const navigate = useNavigate();
  const [incidentNavLoading, setIncidentNavLoading] = useState(false);
  const hasIncident = Boolean(alert.createdIncidentName || alert.createdIncidentSeqId || alert.createdIncidentId);
  const postAlertId = alert.createdAlertSeqId || alert.createdDisasterAlertId;
  const hasDisasterAlert = postAlertId != null;
  if (!hasIncident && !hasDisasterAlert) return null;

  const da = detail && detail.disasterAlert;
  const inc = detail && detail.incident;

  const stillLoading = () =>
    toast.info("Details are still loading — try again in a moment.", {
      position: toast.POSITION.TOP_CENTER,
      autoClose: 2000,
    });

  const openAcknowledge = () => {
    const notificationData = disasterAlertToNotification(da, alert.createdAlertSeqId);
    if (!notificationData) return stillLoading();
    navigate("/Disaster-Acknowladgement", { state: { notificationData, allNotifications: [] } });
  };

  // View Incident renders its lifecycle timeline / category / seq-id from the
  // full incident-list row shape (incidentHistoryStatus, incident_seq_id,
  // current_stage, …), which the alert-detail endpoint's `incident` object
  // doesn't carry — so fetch the matching row from the incidents list before
  // navigating, exactly like Incident Management does. Falls back to the
  // detail's incident object if the list lookup fails.
  const openIncident = async () => {
    const incidentId = (inc && inc.id) != null ? inc.id : alert.createdIncidentId;
    const seqId = alert.createdIncidentSeqId || (inc && inc.incidentSeqId);
    if (incidentId == null && !seqId) return stillLoading();

    const goWith = (data) =>
      navigate("/Disaster-acknowledgereport/situation-awareness/view-incident", { state: { data } });
    const fallback = inc
      ? { ...inc, incident_seq_id: seqId, current_stage: inc.currentStage, current_status: inc.currentStatus }
      : { id: incidentId, incident_seq_id: seqId, name: alert.createdIncidentName };

    setIncidentNavLoading(true);
    try {
      const profile = await getData(EndPoints.getThemeURL);
      const stateId = profile && profile[0] && profile[0].state_id;
      const list = await getData(EndPoints.getincidentcatcreate.replace("STATEID", stateId));
      const row = Array.isArray(list)
        ? list.find(
            (r) =>
              (incidentId != null && String(r.id) === String(incidentId)) ||
              (seqId && (r.incident_seq_id === seqId || r.incidentSeqId === seqId))
          )
        : null;
      goWith(row || fallback);
    } catch (err) {
      console.error("Failed to load incident for navigation:", err);
      goWith(fallback);
    } finally {
      setIncidentNavLoading(false);
    }
  };

  return (
    <div
      style={{
        marginTop: "12px",
        padding: "12px 14px",
        background: "#F0FDF4",
        border: "1px solid #BBF7D0",
        borderRadius: "10px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "12px",
          fontWeight: 700,
          color: "#15803D",
          marginBottom: "10px",
        }}
      >
        <CheckCircle2 size={14} />{" "}
        {hasIncident ? "Incident created from this alert" : "Pre Disaster Alert generated from this alert"}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px 16px" }}>
        {hasDisasterAlert && (
          <MetaField
            label="Post Disaster Alert ID"
            value={
              <LinkValue onClick={openAcknowledge} title="Open in Acknowledge Disaster">
                {postAlertId}
              </LinkValue>
            }
          />
        )}
        {(alert.createdIncidentSeqId || alert.createdIncidentId) && (
          <MetaField label="Incident ID" value={alert.createdIncidentSeqId || alert.createdIncidentId} />
        )}
        {alert.createdIncidentName && (
          <MetaField
            label="Incident Name"
            value={
              <LinkValue onClick={openIncident} title="Open incident" loading={incidentNavLoading}>
                {alert.createdIncidentName}
              </LinkValue>
            }
          />
        )}
      </div>
    </div>
  );
};

// SACHET can send several messages for the same real-world event over time
// (Alert → Update → Cancel); every message in that story shares the same
// chainRootIdentifier. Group the flat API list by that field so the UI can
// show one incident card per event, with its messages listed inside in
// order, instead of a separate card per message.
function groupCapAlertsByChain(alerts) {
  const order = [];
  const groups = new Map();
  alerts.forEach((alert, index) => {
    const key = alert.chainRootIdentifier || alert.capIdentifier || alert.id || `standalone-${index}`;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key).push(alert);
  });

  const MSGTYPE_ORDER = { Alert: 0, Update: 1, Cancel: 2 };
  return order.map((key) => {
    const records = [...groups.get(key)].sort((a, b) => {
      const ta = new Date(a.sent || a.effective || a.onset || 0).getTime();
      const tb = new Date(b.sent || b.effective || b.onset || 0).getTime();
      if (ta !== tb) return ta - tb;
      return (MSGTYPE_ORDER[a.msgType] ?? 9) - (MSGTYPE_ORDER[b.msgType] ?? 9);
    });
    return { key, records };
  });
}

// ── Linked incident detail ────────────────────────────────────────────
// "View on Map" fetches the alert's detail record (the disaster alert +
// incident it was turned into, if any) so the map can drop a pin at the
// incident's location and show its key facts in a popup. Fetched lazily the
// first time the map is opened, then cached for the life of the card.
function useIncidentDetail(endpointTemplate, id, active) {
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const requestedRef = useRef(false);

  useEffect(() => {
    if (!active || !id || requestedRef.current) return;
    requestedRef.current = true;
    let cancelled = false;
    setStatus("loading");
    getData(endpointTemplate.replace("id", id))
      .then((res) => {
        if (cancelled) return;
        const ok = res && !Array.isArray(res) && typeof res === "object";
        setDetail(ok ? res : null);
        setStatus("done");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch alert detail:", err);
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [endpointTemplate, id, active]);

  return { detail, status };
}

// gis_config is a JSON string like {"incidentcenter":[lng, lat]} — note the
// [longitude, latitude] order, the opposite of Leaflet's [lat, lng].
function parseIncidentCenter(gisConfig) {
  if (!gisConfig || typeof gisConfig !== "string") return null;
  try {
    const parsed = JSON.parse(gisConfig);
    const c = parsed && parsed.incidentcenter;
    if (Array.isArray(c) && c.length >= 2) {
      const lng = Number(c[0]);
      const lat = Number(c[1]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
    }
  } catch (_) {
    /* ignore malformed gis_config */
  }
  return null;
}

// Where to drop the map pin from an alert-detail response. Prefers the
// disaster alert's own lat/long, then the incident-centre coordinates in
// gis_config (top-level for CAP, or on the incident for earthquakes).
function incidentLatLng(detail) {
  if (!detail) return null;
  const da = detail.disasterAlert;
  if (da) {
    const lat = Number(da.latitude);
    const lng = Number(da.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  }
  return (
    parseIncidentCenter(detail.gis_config) ||
    parseIncidentCenter(detail.incident && detail.incident.gis_config) ||
    null
  );
}

const popupRowStyle = {
  display: "flex",
  alignItems: "baseline",
  gap: "12px",
  padding: "7px 0",
  borderBottom: "1px solid #EEF2F6",
};

const popupKeyStyle = {
  fontSize: "10.5px",
  fontWeight: 600,
  color: "#94A3B8",
  letterSpacing: "0.01em",
  width: "132px",
  flexShrink: 0,
};

const popupValStyle = { fontSize: "11.5px", fontWeight: 700, color: "#334155", textAlign: "left", wordBreak: "break-word" };

// Maps a free-text severity ("Level 4 – Critical", "Extreme", …) to a soft
// tinted chip so the popup's most important line reads at a glance.
function severityChipColors(text = "") {
  const t = String(text).toLowerCase();
  if (t.includes("critical") || t.includes("extreme") || t.includes("level 4") || t.includes("level 5"))
    return { bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA" };
  if (t.includes("severe") || t.includes("high") || t.includes("level 3"))
    return { bg: "#FFF7ED", fg: "#C2410C", border: "#FED7AA" };
  if (t.includes("moderate") || t.includes("level 2"))
    return { bg: "#FFFBEB", fg: "#B45309", border: "#FDE68A" };
  if (t.includes("minor") || t.includes("low") || t.includes("level 1"))
    return { bg: "#FFF7ED", fg: "#C2410C", border: "#FED7AA" };
  return { bg: "#F1F5F9", fg: "#475569", border: "#E2E8F0" };
}

const popupHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  padding: "10px 34px 10px 14px",
  background: "linear-gradient(180deg, #F5FAFD 0%, #EEF6FA 100%)",
  borderBottom: "1px solid #E1EDF3",
};

// Fallback popup for a CAP alert that hasn't been turned into an app record
// yet (detail endpoint returns capAlert only, disasterAlert/incident null).
const CapAlertPopup = ({ cap }) => {
  const sev = cap.severity;
  const sevChip = severityChipColors(sev);
  const rows = [
    cap.areaDesc ? ["Affected Area", cap.areaDesc] : null,
    cap.certainty ? ["Likelihood", humanize(CERTAINTY_LABELS, cap.certainty)] : null,
    cap.onset ? ["Expected to Begin", formatDateTime(cap.onset)] : null,
    cap.expires ? ["Valid Until", formatDateTime(cap.expires)] : null,
    cap.sender ? ["Issued By", cap.sender] : null,
    cap.capSeqId ? ["Reference ID", cap.capSeqId] : null,
  ].filter(Boolean);

  return (
    <div style={{ width: "384px", background: "#fff" }}>
      <div style={popupHeaderStyle}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 800, color: "#0F2D42" }}>
          <span style={{ width: "22px", height: "22px", borderRadius: "7px", background: "#E1EFF4", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <ShieldAlert size={13} color="#146680" />
          </span>
          {cap.event || "Alert"}
        </span>
        {sev && <span style={{ ...badgeStyle(sevChip.bg, sevChip.fg, sevChip.border), fontSize: "10px" }}>Severity: {sev}</span>}
      </div>
      <div style={{ padding: "8px 14px 12px" }}>
        <div>
          {rows.map(([k, v], i) => (
            <div key={k} style={i === rows.length - 1 ? { ...popupRowStyle, borderBottom: "none" } : popupRowStyle}>
              <span style={popupKeyStyle}>{k}</span>
              <span style={popupValStyle}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Fallback popup for an earthquake alert that hasn't been turned into an app
// record yet (detail endpoint returns earthquakeAlert only).
const EarthquakeAlertPopup = ({ eq }) => {
  const mag = Number(eq.magnitude) || 0;
  const magColor = mag >= 6 ? "#8B0000" : mag >= 4.5 ? "#DC2626" : mag >= 3 ? "#F59E0B" : "#16A34A";
  const place = eq.location || locationFromWarning(eq.warningMessage);
  const rows = [
    place ? ["Location", place] : null,
    eq.effectiveStartTime ? ["Occurred On", formatDateTime(eq.effectiveStartTime)] : null,
    eq.magnitude != null ? ["Magnitude", mag.toFixed(1)] : null,
    eq.depthKm != null ? ["Depth Below Ground", `${eq.depthKm} km`] : null,
    eq.eqSeqId ? ["Reference ID", eq.eqSeqId] : null,
  ].filter(Boolean);

  return (
    <div style={{ width: "384px", background: "#fff" }}>
      <div style={popupHeaderStyle}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 800, color: "#0F2D42" }}>
          <span style={{ width: "22px", height: "22px", borderRadius: "7px", background: "#E1EFF4", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Activity size={13} color="#146680" />
          </span>
          Earthquake
        </span>
        {eq.magnitude != null && (
          <span style={{ ...badgeStyle(`${magColor}18`, magColor, `${magColor}40`), fontSize: "10px" }}>M {mag.toFixed(1)}</span>
        )}
      </div>
      <div style={{ padding: "8px 14px 12px" }}>
        <div>
          {rows.map(([k, v], i) => (
            <div key={k} style={i === rows.length - 1 ? { ...popupRowStyle, borderBottom: "none" } : popupRowStyle}>
              <span style={popupKeyStyle}>{k}</span>
              <span style={popupValStyle}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Compact incident summary rendered inside the map marker's Leaflet popup.
const IncidentDetailPopup = ({ detail }) => {
  const da = (detail && detail.disasterAlert) || {};
  const inc = (detail && detail.incident) || {};
  const hazard = da.hazardType || inc.type || "Incident";
  const severity = inc.severity || da.severity;
  const sevChip = severityChipColors(severity);
  const reported =
    formatLdtArray(da.createdAt) || (inc.created_on ? formatDateTime(inc.created_on) : null);
  const impact = [
    { icon: Building2, label: "Buildings", value: Number(da.buildingCount) || 0 },
    { icon: ShieldAlert, label: "Hospitals", value: Number(da.hospitalCount) || 0 },
    { icon: FileText, label: "Schools", value: Number(da.schoolCount) || 0 },
    { icon: Users, label: "Population", value: Number(da.estimatedPopulation) || 0 },
  ];
  const hasImpact = impact.some((i) => i.value > 0);

  const rows = [
    inc.incidentSeqId ? ["Incident ID", inc.incidentSeqId] : null,
    inc.name ? ["Incident Name", inc.name] : null,
    reported ? ["Created Date and Time", reported] : null,
    inc.currentStatus ? ["Current Status", inc.currentStatus] : null,
    da.measuredValue != null
      ? [
          "Measured",
          `${da.measuredValue}${da.unit ? ` ${da.unit}` : ""}${
            da.thresholdValue != null ? ` (limit ${da.thresholdValue})` : ""
          }`,
        ]
      : null,
    da.regionName || inc.regionIds ? ["Region", da.regionName || `Region ${inc.regionIds}`] : null,
  ].filter(Boolean);

  if (!detail || (!detail.disasterAlert && !detail.incident)) {
    if (detail && detail.capAlert) return <CapAlertPopup cap={detail.capAlert} />;
    if (detail && detail.earthquakeAlert) return <EarthquakeAlertPopup eq={detail.earthquakeAlert} />;
    return <div style={{ fontSize: "12px", color: "#64748B", fontWeight: 600, padding: "4px 2px" }}>No details available for this alert.</div>;
  }

  return (
    <div style={{ width: "384px", background: "#fff" }}>
      {/* header band */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          padding: "10px 34px 10px 14px",
          background: "linear-gradient(180deg, #F5FAFD 0%, #EEF6FA 100%)",
          borderBottom: "1px solid #E1EDF3",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 800, color: "#0F2D42" }}>
          <span
            style={{
              width: "22px",
              height: "22px",
              borderRadius: "7px",
              background: "#E1EFF4",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ShieldAlert size={13} color="#146680" />
          </span>
          {hazard}
        </span>
        {severity && (
          <span style={{ ...badgeStyle(sevChip.bg, sevChip.fg, sevChip.border), fontSize: "10px" }}>{severity}</span>
        )}
      </div>

      <div style={{ padding: "8px 14px 12px" }}>
        <div>
          {rows.map(([k, v], i) => (
            <div key={k} style={i === rows.length - 1 ? { ...popupRowStyle, borderBottom: "none" } : popupRowStyle}>
              <span style={popupKeyStyle}>{k}</span>
              <span style={popupValStyle}>{v}</span>
            </div>
          ))}
        </div>

        {hasImpact && (
          <div
            style={{
              marginTop: "10px",
              padding: "10px 8px",
              background: "#F7FAFC",
              border: "1px solid #EDF2F7",
              borderRadius: "10px",
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "4px",
              textAlign: "center",
            }}
          >
            {impact.map(({ icon: I, label, value }) => (
              <div key={label} title={label}>
                <I size={13} color="#146680" style={{ opacity: 0.9 }} />
                <div style={{ fontSize: "12.5px", fontWeight: 800, color: "#0F2D42", marginTop: "1px" }}>{value}</div>
                <div style={{ fontSize: "8.5px", fontWeight: 600, color: "#94A3B8" }}>{label}</div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

// ── Shared body for one CAP message: headline, meta grid, instruction, map.
// Used both for a standalone alert and for each entry in a multi-message
// incident timeline — severity/msgType badges live in the caller, not here,
// so they're never rendered twice for the same message. ──
const CapAlertBody = ({ alert }) => {
  const [showMap, setShowMap] = useState(false);
  const severityColor = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.Unknown;
  const boundaryPoints = useMemo(() => parseCapPolygonPoints(alert.polygonData), [alert.polygonData]);
  // Fetch the detail record as soon as the card shows a linked incident/alert
  // (so the panel's links work without opening the map first), or when the
  // map is opened.
  const hasLinkedRecord = Boolean(
    alert.createdIncidentSeqId ||
      alert.createdIncidentId ||
      alert.createdIncidentName ||
      alert.createdAlertSeqId ||
      alert.createdDisasterAlertId
  );
  const { detail, status: detailStatus } = useIncidentDetail(
    EndPoints.capAlertDetail,
    alert.id,
    showMap || hasLinkedRecord
  );
  const incidentPos = useMemo(() => incidentLatLng(detail), [detail]);

  return (
    <>
      {alert.headline && (
        <div style={{ fontSize: "13px", fontWeight: 400, color: "#1E293B", marginTop: "12px", lineHeight: 1.5 }}>{alert.headline}</div>
      )}

      <div style={metaPanelStyle}>
        <MetaField label="Affected Area" icon={MapPin} value={alert.areaDesc || "Not specified"} />
        <MetaField label="Urgency" value={humanize(URGENCY_LABELS, alert.urgency)} />
        <MetaField label="Likelihood" value={humanize(CERTAINTY_LABELS, alert.certainty)} />
        <MetaField label="Expected to Begin" icon={Clock} value={formatDateTime(alert.onset)} />
        <MetaField label="Alert Effective From" icon={Clock} value={formatDateTime(alert.effective)} />
        <MetaField label="Alert Valid Until" icon={Clock} value={formatDateTime(alert.expires)} />
      </div>

      <CreatedRecordsPanel alert={alert} detail={detail} />

      <div style={{ display: "flex", alignItems: "stretch", gap: "10px", marginTop: "12px", flexWrap: "wrap" }}>
        {alert.instruction && (
          <div
            style={{
              flex: "1 1 240px",
              display: "flex",
              alignItems: "center",
              padding: "9px 0",
              fontSize: "12px",
              color: "#334155",
              fontWeight: 600,
            }}
          >
            <span style={{ color: "#64748B", marginRight: "4px" }}>Instruction:</span> {alert.instruction}
          </div>
        )}
        <button
          type="button"
          onClick={() => setShowMap((v) => !v)}
          style={{ ...mapToggleBtnStyle(showMap), marginLeft: alert.instruction ? 0 : "auto" }}
          title="See the affected area plotted on a map"
        >
          <MapIconLucide size={13} />
          {showMap ? "Hide map" : "View on Map"}
        </button>
      </div>

      {showMap && (
        <>
          <AlertPolygonMap
            polygons={[{ positions: boundaryPoints, color: severityColor, fillOpacity: 0.15 }]}
            incidentMarker={incidentPos ? { position: incidentPos, render: <IncidentDetailPopup detail={detail} /> } : undefined}
          />
          <MapDetailNote status={detailStatus} hasIncident={Boolean(incidentPos)} />
        </>
      )}
    </>
  );
};

// Small hint shown under the map about the alert-detail lookup.
const MapDetailNote = ({ status, hasIncident }) => {
  let text = null;
  if (status === "loading") text = "Loading alert details…";
  else if (status === "done" && hasIncident) text = "Tap the pin for details.";
  else if (status === "done" && !hasIncident) text = "No map location available for this alert.";
  else if (status === "error") text = "Couldn't load the alert details.";
  if (!text) return null;
  return (
    <div style={{ marginTop: "6px", fontSize: "10.5px", fontWeight: 600, color: "#94A3B8", display: "flex", alignItems: "center", gap: "5px" }}>
      {status === "loading" && <Loader2 size={11} className="spin-caa" />}
      {status === "done" && hasIncident && <MapPin size={11} color="#146680" />}
      {text}
    </div>
  );
};

// ── One message (Alert/Update/Cancel) within a multi-message incident's
// timeline — only used when an incident actually has more than one message,
// so the msgType/severity/timestamp row only appears where it adds
// information beyond the incident header. ──
const CapMessageRow = ({ alert, isLast }) => {
  const severityColor = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.Unknown;
  const msgTypeStyle = MSGTYPE_COLORS[alert.msgType] || MSGTYPE_COLORS.Alert;

  return (
    <div style={{ paddingTop: "12px", marginTop: "12px", borderTop: "1px dashed #E9EEF3" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
          <span style={badgeStyle(msgTypeStyle.bg, msgTypeStyle.fg, msgTypeStyle.border)}>{humanize(MSGTYPE_LABELS, alert.msgType, "Alert")}</span>
          <span style={badgeStyle(`${severityColor}18`, severityColor, `${severityColor}40`)}>Severity: {alert.severity || "Unknown"}</span>
        </div>
        <div style={{ fontSize: "11px", color: "#94A3B8", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}>
          <Clock size={11} /> {formatDateTime(alert.sent || alert.effective || alert.onset)}
        </div>
      </div>
      <CapAlertBody alert={alert} />
    </div>
  );
};

// ── One incident card: every Alert/Update/Cancel message that shares a
// chainRootIdentifier. A single-message incident renders exactly like a
// plain alert card; a multi-message incident adds a per-message timeline
// underneath so the severity/status badge is never shown twice for the
// same message. ──
const CapIncidentCard = ({ records }) => {
  const latest = records[records.length - 1];
  const { Icon, color } = eventIcon(latest.event);
  const severityColor = SEVERITY_COLORS[latest.severity] || SEVERITY_COLORS.Unknown;
  const isChain = records.length > 1;

  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Same rule as the earthquake cards: once Sachet marks the latest message
  // CONVERTED, an alert already exists for it in the app.
  const alreadyConverted = latest.reviewStatus === "CONVERTED" || created;
  // Only show the "Incident created" badge once an incident actually exists
  // for this alert — CONVERTED alone can mean it was reviewed without a
  // linked incident (createdIncidentName stays null).
  const incidentCreated = Boolean(latest.createdIncidentName || latest.createdIncidentSeqId || latest.createdIncidentId);
  // "ALERT_ONLY" means the review generated a pre-disaster alert but no
  // incident — flag that separately so the header still shows what happened.
  const preAlertOnly =
    !incidentCreated &&
    (latest.createdStatus === "ALERT_ONLY" ||
      latest.createdAlertSeqId != null ||
      latest.createdDisasterAlertId != null);

  const handleCreateAlert = async () => {
    if (creating || alreadyConverted || !latest.id) return;
    setCreating(true);
    const res = await postDataWithBody(
      EndPoints.createAlertFromCapAlert.replace("id", latest.id),
      {},
      {}
    );
    setCreating(false);
    setConfirmOpen(false);
    if (res && res.status === "success") {
      setCreated(true);
      toast.success(res.msg || "Alert created successfully.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } else {
      toast.error(res?.msg || "Failed to create alert.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", minWidth: 0 }}>
          <div
            style={{
              width: "38px",
              height: "38px",
              borderRadius: "50%",
              background: `${color}18`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon size={18} color={color} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "14px", fontWeight: 800, color: "#0F2D42" }}>{latest.event || "Alert"}</div>
            <div style={{ fontSize: "11px", color: "#7A9BB0", marginTop: "1px", display: "flex", alignItems: "center", gap: "5px" }}>
              <Radio size={11} /> Issued by {latest.sender || "unknown source"}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: "6px", alignItems: "center", flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {latest.capSeqId && (
            <span
              style={{
                ...badgeStyle("#F1F5F9", "#475569", "#E2E8F0"),
                fontFamily: "'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
              title="SACHET reference ID"
            >
              ID: {latest.capSeqId}
            </span>
          )}
          <span style={badgeStyle(`${severityColor}18`, severityColor, `${severityColor}40`)}>Severity: {latest.severity || "Unknown"}</span>
          {isChain && (
            <span style={badgeStyle("#F1F5F9", "#475569", "#E2E8F0")}>{records.length} messages</span>
          )}
          {incidentCreated && (
            <span style={badgeStyle("#F0FDF4", "#15803D", "#BBF7D0")}>
              <CheckCircle2 size={11} /> Incident created
            </span>
          )}
          {preAlertOnly && (
            <span style={badgeStyle("#F0FDF4", "#15803D", "#BBF7D0")}>
              <CheckCircle2 size={11} /> Pre Disaster Alert Generated
            </span>
          )}
          {!alreadyConverted && (
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={creating}
              style={createAlertBtnStyle(creating, created)}
              title="Create an alert in the app from this incident"
            >
              {creating ? (
                <Loader2 size={11} className="spin-caa" />
              ) : (
                <FilePlus2 size={11} />
              )}
              {creating ? "Creating…" : "Create Alert"}
            </button>
          )}
        </div>
      </div>

      {isChain ? (
        records.map((alert, i) => (
          <CapMessageRow key={alert.id ?? alert.capIdentifier ?? i} alert={alert} isLast={i === records.length - 1} />
        ))
      ) : (
        <CapAlertBody alert={latest} />
      )}

      <Modal
        show={confirmOpen}
        onHide={() => !creating && setConfirmOpen(false)}
        centered
        backdrop="static"
        keyboard={!creating}
      >
        <Modal.Header closeButton={!creating} style={{ borderBottom: "1px solid #EEF2F6" }}>
          <Modal.Title style={{ fontSize: "16px", fontWeight: 800, color: "#0F2D42", display: "flex", alignItems: "center", gap: "8px" }}>
            <FilePlus2 size={17} color="#146680" />
            Create Alert
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "13px", color: "#334155", lineHeight: 1.6 }}>
          This will create an alert in the app for the <strong>{latest.event || "alert"}</strong> issued by{" "}
          <strong>{latest.sender || "an unknown source"}</strong>. Do you want to continue?
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #EEF2F6", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <button type="button" onClick={() => setConfirmOpen(false)} disabled={creating} style={confirmModalBtnStyle(false, creating)}>
            Cancel
          </button>
          <button type="button" onClick={handleCreateAlert} disabled={creating} style={confirmModalBtnStyle(true, creating)}>
            {creating && <Loader2 size={13} className="spin-caa" />}
            {creating ? "Creating…" : "Yes, Create Alert"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// ── One earthquake alert card ──────────────────────────────────────────
const EarthquakeAlertCard = ({ alert }) => {
  const [showMap, setShowMap] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const zones = useMemo(() => safeParseIntensityZones(alert.polygonData), [alert.polygonData]);
  // Once Sachet marks this record CONVERTED, an alert already exists for it
  // in the app — the create action no longer applies, so hide the button
  // rather than just disabling it.
  const alreadyConverted = alert.reviewStatus === "CONVERTED" || created;
  const magnitude = Number(alert.magnitude) || 0;
  const magColor = magnitude >= 6 ? "#8B0000" : magnitude >= 4.5 ? "#DC2626" : magnitude >= 3 ? "#F59E0B" : "#16A34A";
  const epicenter = Number.isFinite(Number(alert.latitude)) && Number.isFinite(Number(alert.longitude))
    ? [Number(alert.latitude), Number(alert.longitude)]
    : null;
  const locationName =
    alert.location || locationFromWarning(alert.warningMessage) || "Not available";
  const incidentCreated = Boolean(
    alert.createdIncidentName || alert.createdIncidentSeqId || alert.createdIncidentId
  );
  const preAlertOnly =
    !incidentCreated &&
    (alert.createdStatus === "ALERT_ONLY" ||
      alert.createdAlertSeqId != null ||
      alert.createdDisasterAlertId != null);
  const hasLinkedRecord = incidentCreated || preAlertOnly;
  const { detail, status: detailStatus } = useIncidentDetail(
    EndPoints.earthquakeAlertDetail,
    alert.id,
    showMap || hasLinkedRecord
  );
  const incidentPos = useMemo(() => incidentLatLng(detail), [detail]);
  // Rings are listed widest-first in the feed, so drawing in that order
  // naturally layers the smaller, more-severe zones on top. The outermost
  // "felt" ring often ships with color "transparent" — swap in a faint grey
  // outline instead so that boundary still shows up on the map.
  const mapPolygons = useMemo(
    () =>
      zones.flatMap((z) => {
        const isTransparent = z.color === "transparent";
        return z.rings.map((ring) => ({
          positions: ring,
          color: isTransparent ? "#94A3B8" : z.color,
          fillOpacity: isTransparent ? 0.04 : 0.22,
        }));
      }),
    [zones]
  );

  const handleCreateAlert = async () => {
    if (creating || created || !alert.id) return;
    setCreating(true);
    const res = await postDataWithBody(
      EndPoints.createAlertFromEarthquake.replace("id", alert.id),
      {},
      {}
    );
    setCreating(false);
    setConfirmOpen(false);
    if (res && res.status === "success") {
      setCreated(true);
      toast.success(res.msg || "Alert created successfully.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } else {
      toast.error(res?.msg || "Failed to create alert.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", minWidth: 0 }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: `${magColor}15`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: "15px", fontWeight: 800, color: magColor, lineHeight: 1 }}>{magnitude.toFixed(1)}</span>
            <span style={{ fontSize: "8px", fontWeight: 700, color: magColor }}>MAG</span>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "14px", fontWeight: 800, color: "#0F2D42" }}>Earthquake</div>
            <div style={{ fontSize: "11px", color: "#7A9BB0", marginTop: "1px", display: "flex", alignItems: "center", gap: "5px" }}>
              <Activity size={11} /> {alert.depthKm != null ? `${alert.depthKm} km below ground` : "Depth not known"}
              <span style={{ color: "#CBD5E1" }}>·</span>
              {formatDateTime(alert.effectiveStartTime)}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: "6px", alignItems: "center", flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {alert.eqSeqId && (
            <span
              style={{
                ...badgeStyle("#F1F5F9", "#475569", "#E2E8F0"),
                fontFamily: "'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
              title="SACHET reference ID"
            >
              ID: {alert.eqSeqId}
            </span>
          )}
          {incidentCreated && (
            <span style={badgeStyle("#F0FDF4", "#15803D", "#BBF7D0")}>
              <CheckCircle2 size={11} /> Incident created
            </span>
          )}
          {preAlertOnly && (
            <span style={badgeStyle("#F0FDF4", "#15803D", "#BBF7D0")}>
              <CheckCircle2 size={11} /> Pre Disaster Alert Generated
            </span>
          )}
          {!alreadyConverted && (
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={creating}
              style={{ ...createAlertBtnStyle(creating, created), flexShrink: 0 }}
              title="Create an alert in the app from this earthquake"
            >
              {creating ? (
                <Loader2 size={11} className="spin-caa" />
              ) : (
                <FilePlus2 size={11} />
              )}
              {creating ? "Creating…" : "Create Alert"}
            </button>
          )}
        </div>
      </div>

      {alert.warningMessage && (
        <div style={{ fontSize: "13px", fontWeight: 400, color: "#1E293B", marginTop: "12px", lineHeight: 1.5 }}>{alert.warningMessage}</div>
      )}

      <div style={metaPanelStyle}>
        <div>
          <div style={metaLabelStyle}>
            <MapPin size={10} style={{ marginRight: "3px", verticalAlign: "-1px" }} />
            Location
          </div>
          <div style={metaValueStyle}>{locationName}</div>
        </div>
        <div>
          <div style={metaLabelStyle}>Depth Below Ground</div>
          <div style={metaValueStyle}>{alert.depthKm != null ? `${alert.depthKm} km` : "Not known"}</div>
        </div>
        <div>
          <div style={metaLabelStyle}>
            <Zap size={10} style={{ marginRight: "3px", verticalAlign: "-1px" }} />
            Magnitude
          </div>
          <div style={{ ...metaValueStyle, color: magColor, fontWeight: 800 }}>{magnitude.toFixed(1)}</div>
        </div>
      </div>

      <CreatedRecordsPanel alert={alert} detail={detail} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginTop: "12px", paddingTop: "10px", borderTop: "1px solid #F1F5F9" }}>
        <button type="button" onClick={() => setShowMap((v) => !v)} style={mapToggleBtnStyle(showMap)} title="See the shaking zones plotted on a map">
          <MapIconLucide size={13} />
          {showMap ? "Hide map" : "View on Map"}
        </button>
      </div>

      {showMap && (
        <>
          <AlertPolygonMap
            polygons={mapPolygons}
            markerPosition={epicenter}
            incidentMarker={incidentPos ? { position: incidentPos, render: <IncidentDetailPopup detail={detail} /> } : undefined}
          />
          <MapDetailNote status={detailStatus} hasIncident={Boolean(incidentPos)} />
        </>
      )}

      <Modal
        show={confirmOpen}
        onHide={() => !creating && setConfirmOpen(false)}
        centered
        backdrop="static"
        keyboard={!creating}
      >
        <Modal.Header closeButton={!creating} style={{ borderBottom: "1px solid #EEF2F6" }}>
          <Modal.Title style={{ fontSize: "16px", fontWeight: 800, color: "#0F2D42", display: "flex", alignItems: "center", gap: "8px" }}>
            <FilePlus2 size={17} color="#146680" />
            Create Alert
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "13px", color: "#334155", lineHeight: 1.6 }}>
          This will create an alert in the app for the magnitude <strong>{magnitude.toFixed(1)}</strong> earthquake at{" "}
          <strong>{alert.location || "this location"}</strong>. Do you want to continue?
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #EEF2F6", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <button type="button" onClick={() => setConfirmOpen(false)} disabled={creating} style={confirmModalBtnStyle(false, creating)}>
            Cancel
          </button>
          <button type="button" onClick={handleCreateAlert} disabled={creating} style={confirmModalBtnStyle(true, creating)}>
            {creating && <Loader2 size={13} className="spin-caa" />}
            {creating ? "Creating…" : "Yes, Create Alert"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const emptyStateStyle = {
  ...cardStyle,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  padding: "36px 20px",
  color: "#94A3B8",
  fontSize: "12.5px",
  fontWeight: 600,
  textAlign: "center",
};

const TABS = [
  { key: "cap", label: "Weather & Hazard Alerts", icon: Radio, description: "Avalanche, rainfall and storm warnings for Ladakh" },
  { key: "earthquake", label: "Earthquake", icon: Activity, description: "Recent earthquake activity and affected areas" },
];

const tabBtnStyle = (active) => ({
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "9px 16px",
  borderRadius: "10px",
  border: active ? "1.5px solid #146680" : "1.5px solid #E9EEF3",
  background: active ? "#146680" : "#fff",
  color: active ? "#fff" : "#334155",
  fontSize: "12.5px",
  fontWeight: 700,
  cursor: "pointer",
  transition: "all 0.15s ease",
});

const tabCountStyle = (active) => ({
  fontSize: "10.5px",
  fontWeight: 800,
  padding: "1px 7px",
  borderRadius: "999px",
  background: active ? "rgba(255,255,255,0.22)" : "#F1F5F9",
  color: active ? "#fff" : "#5B7A93",
});

const CapAlertsView = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("cap");
  const [capCategory, setCapCategory] = useState("all");
  const [capAlerts, setCapAlerts] = useState([]);
  const [earthquakeAlerts, setEarthquakeAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // A "silent" refresh (auto-refresh) updates the data in place without
  // dropping back to the full-page loading state or wiping the list on a
  // transient failure. Every path — first load, manual Refresh and
  // auto-refresh — also hits /config/server-time so "Last Updated" always
  // reflects the server clock.
  const fetchAll = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    if (!silent) setError(null);
    try {
      const [capRes, eqRes, timeRes] = await Promise.all([
        getData(EndPoints.sachetLadakhCapAlerts),
        getData(EndPoints.sachetEarthquakeAlerts),
        getData(EndPoints.getServerDateAndTime).catch(() => null),
      ]);
      setCapAlerts(Array.isArray(capRes) ? capRes : []);
      setEarthquakeAlerts(Array.isArray(eqRes) ? eqRes : []);
      setLastUpdated(timeRes && timeRes.serverTime ? new Date(timeRes.serverTime) : new Date());
      setError(null);
    } catch (err) {
      console.error("Failed to fetch CAP/earthquake alerts:", err);
      if (!silent) setError("Couldn't reach the alerts service. Check the connection and try again.");
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Auto-refresh every 5 minutes.
  useEffect(() => {
    const id = setInterval(() => fetchAll({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchAll]);

  const capIncidents = useMemo(() => groupCapAlertsByChain(capAlerts), [capAlerts]);

  const capCategoryCounts = useMemo(() => {
    const counts = {};
    capIncidents.forEach((g) => {
      const cat = capEventCategory(g.records[g.records.length - 1].event);
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [capIncidents]);

  const filteredCapIncidents = useMemo(
    () =>
      capCategory === "all"
        ? capIncidents
        : capIncidents.filter((g) => capEventCategory(g.records[g.records.length - 1].event) === capCategory),
    [capIncidents, capCategory]
  );

  // The "All" hazard tab also lists earthquake alerts, so its count and its
  // list both fold in earthquakeAlerts.
  const showEarthquakesInCap = activeTab === "cap" && capCategory === "all";
  const activeList =
    activeTab === "cap"
      ? showEarthquakesInCap
        ? [...filteredCapIncidents, ...earthquakeAlerts]
        : filteredCapIncidents
      : earthquakeAlerts;
  const activeMeta = TABS.find((t) => t.key === activeTab);
  const capCategoryLabel = (CAP_CATEGORIES.find((c) => c.key === capCategory) || {}).label;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      {/* ── Hero banner ── */}
      <div
        style={{
          background: "linear-gradient(120deg, #146680 0%, #1C4E72 100%)",
          borderRadius: "14px",
          padding: "14px 18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          boxShadow: "0 6px 16px rgba(20,102,128,0.22)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            onClick={() => navigate(-1)}
            title="Back"
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              background: "rgba(255,255,255,0.15)",
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              cursor: "pointer",
            }}
          >
            <ArrowLeft size={18} color="#fff" />
          </button>
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "10px",
              background: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AlertOctagon size={18} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize: "15px", fontWeight: 800, color: "#fff", letterSpacing: "-0.01em" }}>Hazard &amp; Earthquake Alerts</div>
            <div style={{ fontSize: "11.5px", color: "rgba(255,255,255,0.78)", marginTop: "2px" }}>
              Via SACHET (NDMA) · sourced from IMD, DGRE, SDMA &amp; global seismic data
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "14px", flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <span
            title="Auto-refreshes every 5 minutes"
            style={{
              fontSize: "11.5px",
              fontWeight: 600,
              color: "#fff",
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              cursor: "default",
            }}
          >
            {refreshing && <Loader2 size={11} className="spin-caa" />}
            Last Refreshed: {lastUpdated ? formatServerStamp(lastUpdated) : "—"}
          </span>
          <button
            type="button"
            onClick={() => fetchAll()}
            disabled={loading}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 14px",
              borderRadius: "9px",
              border: "1px solid rgba(255,255,255,0.3)",
              background: "rgba(255,255,255,0.12)",
              color: "#fff",
              fontSize: "12px",
              fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              flexShrink: 0,
            }}
          >
            {loading ? <Loader2 size={13} className="spin-caa" /> : <RefreshCw size={13} />}
            Refresh
          </button>
        </div>
      </div>

      {/* ── Tabs: hazard categories + earthquake, one row ── */}
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {CAP_CATEGORIES.map((cat) => {
          const active = activeTab === "cap" && cat.key === capCategory;
          const count =
            cat.key === "all" ? capIncidents.length + earthquakeAlerts.length : capCategoryCounts[cat.key] || 0;
          return (
            <button
              key={cat.key}
              type="button"
              onClick={() => {
                setActiveTab("cap");
                setCapCategory(cat.key);
              }}
              style={tabBtnStyle(active)}
            >
              {cat.key === "all" && <Radio size={14} />}
              {cat.label}
              <span style={tabCountStyle(active)}>{count}</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setActiveTab("earthquake")}
          style={tabBtnStyle(activeTab === "earthquake")}
        >
          <Activity size={14} />
          Earthquake
          <span style={tabCountStyle(activeTab === "earthquake")}>{earthquakeAlerts.length}</span>
        </button>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div style={emptyStateStyle}>
          <Loader2 size={22} className="spin-caa" color="#146680" />
          Loading {activeMeta.label.toLowerCase()}…
        </div>
      ) : error ? (
        <div style={{ ...emptyStateStyle, color: "#DC2626" }}>
          <TriangleAlert size={22} color="#DC2626" />
          {error}
          <button
            type="button"
            onClick={() => fetchAll()}
            style={{
              marginTop: "6px",
              padding: "7px 16px",
              borderRadius: "8px",
              border: "1px solid #DC2626",
              background: "#fff",
              color: "#DC2626",
              fontSize: "11.5px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      ) : activeList.length === 0 ? (
        <div style={emptyStateStyle}>
          <Radio size={22} color="#94A3B8" />
          {activeTab === "cap" && capCategory !== "all"
            ? `No ${capCategoryLabel.toLowerCase()} alerts available right now.`
            : `No ${activeMeta.label.toLowerCase()} available right now.`}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {activeTab === "cap" ? (
            <>
              {filteredCapIncidents.map((group) => (
                <CapIncidentCard key={group.key} records={group.records} />
              ))}
              {showEarthquakesInCap &&
                earthquakeAlerts.map((alert) => (
                  <EarthquakeAlertCard key={alert.id ?? alert.dedupKey} alert={alert} />
                ))}
            </>
          ) : (
            earthquakeAlerts.map((alert) => <EarthquakeAlertCard key={alert.id ?? alert.dedupKey} alert={alert} />)
          )}
        </div>
      )}

      <style>{`
        @keyframes spin-caa { to { transform: rotate(360deg); } }
        .spin-caa { animation: spin-caa 0.8s linear infinite; }
        .cap-incident-popup .leaflet-popup-content-wrapper { border-radius: 14px; box-shadow: 0 12px 34px rgba(15,45,66,0.20); overflow: hidden; padding: 0; }
        .cap-incident-popup .leaflet-popup-content { margin: 0; line-height: 1.4; width: 384px !important; }
        .cap-incident-popup .leaflet-popup-tip { box-shadow: 0 12px 34px rgba(15,45,66,0.20); }
        .cap-incident-popup a.leaflet-popup-close-button { top: 8px; right: 8px; color: #64748B; }
        .cap-incident-popup a.leaflet-popup-close-button:hover { color: #0F2D42; }
      `}</style>
    </div>
  );
};

const CapAlerts = () => {
  const layoutData = [
    {
      x: 0,
      y: 0,
      width: 12,
      height: 20,
      id: 0,
      component: (
        <div style={{ padding: "20px", overflowY: "auto", height: "100%", boxSizing: "border-box" }}>
          <CapAlertsView />
        </div>
      ),
    },
  ];

  return (
    <div className="grid-stack grid-stack-instance-1">
      <GridItem layoutData={layoutData} />
    </div>
  );
};

export default CapAlerts;
